/**
 * An input row that obeys v2's edit lock, and the control that unlocks it.
 *
 * WHY THIS EXISTS. The shared `EditableDataRow` disables every HISTORICAL period, which is
 * right for v1: history is entered once and then protected from a stray keystroke. It is wrong
 * for a v2 model that came from a PDF, and wrong in the worst way — an import spans only the
 * years the filing reports, so every period is historical, so every cell was read-only. The
 * figures most likely to need correcting were the only ones that could not be touched, and the
 * import's own panel would name a figure the filing disagreed with while the user had no way to
 * change it.
 *
 * So the protection stays and becomes deliberate: locked by default, unlocked by a visible
 * control, and locked again on reload. Nothing is silently editable, and nothing is permanently
 * frozen.
 *
 * The lock lives in the v2 store rather than being threaded through six views as a prop, and
 * this wrapper reads it, so a view swaps one import and is done.
 */

import type { Quarter, QuarterlyData } from '../../../types/financial-model';
import { EditableDataRow } from '../table/DataRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';

export function V2EditableRow(props: {
  label: string;
  data: QuarterlyData;
  onCellChange: (quarter: Quarter, value: number) => void;
  className?: string;
}) {
  const unlocked = useFinancialModelV2Store((s) => s.editingUnlocked);
  // One click opens a figure, as Baseline Accounting's input boxes do - locked or not, an
  // editable figure in v2 looks and behaves like a field you type into.
  return <EditableDataRow {...props} allowHistorical={unlocked} editOnClick />;
}

/**
 * The lock itself, shown wherever figures are entered.
 *
 * Deliberately plain about what it does: "actuals" rather than "history", because the figures
 * it protects are the ones read off a filing, and that is what the user is deciding whether to
 * overwrite.
 */
export function V2EditLock() {
  const unlocked = useFinancialModelV2Store((s) => s.editingUnlocked);
  const setUnlocked = useFinancialModelV2Store((s) => s.setEditingUnlocked);

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={unlocked ? 'text-amber-800' : 'text-gray-500'}>
        {unlocked
          ? 'Actuals unlocked — the filed figures can be changed'
          : 'Actuals locked — the filed figures are read-only'}
      </span>
      <button
        type="button"
        onClick={() => setUnlocked(!unlocked)}
        className={`rounded border px-2 py-0.5 font-medium ${
          unlocked
            ? 'border-amber-400 bg-amber-50 text-amber-900'
            : 'border-gray-300 bg-white text-gray-700'
        }`}
      >
        {unlocked ? 'Lock' : 'Unlock to edit'}
      </button>
    </div>
  );
}
